import store from './store';
import * as utils from '../utils/request';
import _ from 'lodash';

/**
* @typedef {Object} FundingItem
* @property {string} id
* @property {string} name
* @property {string} description
* @property {number} amount
*/

/**
* @typedef {Object} Funding
* @property {string} id
* @property {number} createdAt
* @property {AccountSnippet} createdBy
* @property {number} resolvedAt
* @property {AccountSnippet} resolvedBy
* @property {string} organization
* @property {string} beneficiary
* @property {string} name
* @property {string} description
* @property {number} amount
* @property {number} raised
* @property {FundingItem[]} items
*/

store.registerModule(
  'fundings',
  {
    namespaced: true,
    actions: {
      /**
       * @param ctx
       * @param {Object} opts
       * @param {string} opts.beneficiary
       * @returns {Funding[]}
       */
      async get (ctx, opts) {
        opts = Object.assign({}, opts);
        if (!opts.beneficiary) throw new Error(`Beneficiary is required`);

        const searchParams = { ...opts };

        const funding = await utils.request(
          `fundings`,
          {
            method: 'get',
            searchParams
          }
        );

        return funding;
      },

      /**
       * @param ctx
       * @param {Object} opts
       * @param {string} opts.beneficiary
       * @param {string} opts.organization
       * @param {string} opts.name
       * @param {string} opts.description
       * @param {Object[]} opts.items
       * @param {string} opts.items.name
       * @param {string} opts.items.amount
       */
      async create (ctx, opts) {
        const body = { ...opts };
        const amountList = [];
        _.each(
          _.get(body, 'items'),
          item => amountList.push(item.amount)
        );
        const total = _.sum(amountList);

        const funding = await utils.request(
          `fundings`,
          {
            method: 'post',
            body: {
              beneficiary: body.beneficiary,
              // organization: body.organization,
              description: body.description,
              name: body.name,
              amount: total,
              items: body.items
            }
          }
        );

        await store.dispatch(
          'beneficiaries/update',
          {
            id: body.beneficiary,
            activeFunding: funding.id
          }
        );

        return funding;
      },

      /**
       * @param ctx
       * @param {Object} opts
       * @param {string} opts.id
       * @param {number} opts.amount
       * @param {Object[]} opts.items
       * @param {string} opts.items.name
       * @param {string} opts.items.amount
       * @param {string} opts.items.description
       */
      async addItem (ctx, opts) {
        const body = { ...opts };
        const amountList = [];
        _.each(
          _.get(body, 'items'),
          value => amountList.push(value.amount)
        );
        const total = _.sum(_.flatten([amountList, body.amount]));

        await utils.request(
          `fundings/${opts.id}`,
          {
            method: 'put',
            body: {
              amount: total,
              items: body.items
            }
          }
        );
      },

      /**
       * @param ctx
       * @param {Object} opts
       * @param {string} opts.id
       * @param {string} opts.name
       * @param {string} opts.description
       * @returns {Funding}
       */
      async update (ctx, opts) {
        const body = { ..._.omit(opts, 'id') };

        const funding = await utils.request(
          `fundings/${opts.id}`,
          {
            method: 'put',
            body
          }
        );

        return funding;
      },

      /**
       * @param ctx
       * @param {Object} opts
       * @param {string} opts.id
       * @param {string} opts.beneficiary
       * @param {string} opts.$resolve
       */
      async resolve (ctx, opts) {
        await utils.request(
          `fundings/${opts.id}`,
          {
            method: 'put',
            body: {
              $resolve: opts.$resolve
            }
          }
        );

        const activeFunding = opts.$resolve ? '' : opts.id;

        await store.dispatch(
          'beneficiaries/update',
          {
            id: opts.beneficiary,
            activeFunding
          }
        );
      },

      /**
       * @param ctx
       * @param {Object} opts
       * @param {string} opts.id
       */
      async delete (ctx, opts) {
        await utils.request(
          `fundings/${opts.id}`,
          {
            method: 'delete'
          }
        );
      }
    }
  }
);
